// /accounts/$a/tenants/$t/sites/$siteSlug/ (index) — overview card +
// inline floors panel. Header + tabs live on SiteLayout.

import { useQuery } from "@tanstack/react-query";

import { sites } from "../../api/endpoints/sites";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import { CopyButton } from "../../components/ui/CopyButton";
import { ErrorBanner } from "../../components/ui/ErrorBanner";

import { FloorListPanel } from "../floors/FloorListPanel";

interface SiteDetailPageProps {
  accountSlug: string;
  tenantSlug: string;
  siteSlug: string;
}

export function SiteDetailPage({
  accountSlug,
  tenantSlug,
  siteSlug,
}: SiteDetailPageProps) {
  const q = useQuery({
    ...adminQueryOptions,
    queryKey: qk.site(accountSlug, tenantSlug, siteSlug),
    queryFn: () =>
      sites.get(accountSlug, tenantSlug, siteSlug).then((r) => r.data),
  });

  if (q.isPending) return <p style={{ color: "var(--text-dim)" }}>Loading…</p>;
  if (q.error) return <ErrorBanner error={q.error} title="Failed to load site" />;
  if (!q.data) return null;

  const geo =
    q.data.geo_lat != null && q.data.geo_lon != null
      ? `${q.data.geo_lat}, ${q.data.geo_lon}`
      : null;

  return (
    <>
      <section style={card}>
        <dl style={dl}>
          <dt>Address</dt>
          <dd>{q.data.address ?? "—"}</dd>
          <dt>City / Country</dt>
          <dd>
            {[q.data.city, q.data.country].filter(Boolean).join(", ") || "—"}
          </dd>
          <dt>Geo</dt>
          <dd>{geo ?? "—"}</dd>
          <dt>Edge host</dt>
          <dd>
            {q.data.edge_host_fqdn ? (
              <code>{q.data.edge_host_fqdn}</code>
            ) : (
              "—"
            )}
          </dd>
          <dt>HA instance id</dt>
          <dd>
            {q.data.ha_instance_id ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <code style={{ fontSize: "0.78rem" }}>{q.data.ha_instance_id}</code>
                <CopyButton value={q.data.ha_instance_id} label="Copy" />
              </span>
            ) : (
              "Unbound"
            )}
          </dd>
          <dt>Status</dt>
          <dd>{q.data.status}</dd>
        </dl>
      </section>

      <section style={{ marginTop: 24 }}>
        <FloorListPanel
          accountSlug={accountSlug}
          tenantSlug={tenantSlug}
          siteSlug={siteSlug}
        />
      </section>
    </>
  );
}

const card: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  padding: 16,
};

const dl: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "max-content 1fr",
  gap: "6px 16px",
  margin: 0,
  fontSize: "0.9rem",
};
